/**
 * Drones For Good — project overlay layer.
 * Loads curated project data and renders color-coded dots on the globe.
 * Includes a legend panel toggled from the layer panel.
 */
import mapboxgl from 'mapbox-gl';
import { fetchJSON } from '../utils/fetch-data';

// ── Types ──────────────────────────────────────────────────────────────────

export interface DroneProject {
  name: string;
  lat: number;
  lng: number;
  country: string;
  org: string;
  type: ProjectType;
  description: string;
  url: string;
}

export type ProjectType =
  | 'conservation'
  | 'medical_delivery'
  | 'humanitarian_mapping'
  | 'agriculture'
  | 'drone_corridor'
  | 'disaster_response'
  | 'drone_journalism'
  | 'community_events';

// ── Colors & Labels ────────────────────────────────────────────────────────

export const PROJECT_TYPE_CONFIG: Record<ProjectType, { color: string; label: string; icon: string }> = {
  conservation:         { color: '#22c55e', label: 'Conservation',         icon: '🌿' },
  medical_delivery:     { color: '#ef4444', label: 'Medical Delivery',     icon: '🏥' },
  humanitarian_mapping: { color: '#3b82f6', label: 'Humanitarian Mapping', icon: '🗺️' },
  agriculture:          { color: '#f59e0b', label: 'Agriculture',          icon: '🌾' },
  drone_corridor:       { color: '#a855f7', label: 'Drone Corridors',      icon: '🛫' },
  disaster_response:    { color: '#f97316', label: 'Disaster Response',    icon: '🆘' },
  drone_journalism:     { color: '#06b6d4', label: 'Drone Journalism',     icon: '📰' },
  community_events:     { color: '#e879f9', label: 'Community & Events',   icon: '🤝' },
};

// ── State ──────────────────────────────────────────────────────────────────

const SOURCE_ID = 'drone-projects';
const LAYER_ID  = 'drone-projects-dots';
const LAYER_PULSE = 'drone-projects-pulse';

let projectData: GeoJSON.FeatureCollection | null = null;
let rawProjects: DroneProject[] | null = null;
let activePopup: mapboxgl.Popup | null = null;
let legendEl: HTMLElement | null = null;
let hoverPopup: mapboxgl.Popup | null = null;

// ── Data loading ───────────────────────────────────────────────────────────

async function loadRaw(): Promise<DroneProject[]> {
  if (rawProjects) return rawProjects;
  rawProjects = await fetchJSON<DroneProject[]>('/data/drone_projects.json');
  return rawProjects;
}

async function loadProjects(): Promise<GeoJSON.FeatureCollection> {
  if (projectData) return projectData;

  const raw = await loadRaw();

  projectData = {
    type: 'FeatureCollection',
    features: raw.map(p => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [p.lng, p.lat] },
      properties: {
        name: p.name,
        org: p.org,
        type: p.type,
        description: p.description,
        url: p.url,
        country: p.country,
      },
    })),
  };
  return projectData;
}

/** Get all drone-for-good projects for a specific country (by ISO2 code). */
export async function getProjectsByCountry(iso2: string): Promise<DroneProject[]> {
  const all = await loadRaw();
  const code = iso2.toUpperCase();
  return all.filter(p => p.country === code);
}

// ── Click popup ──────────────────────────────────────────────────────────

function onProjectClick(e: mapboxgl.MapLayerMouseEvent) {
  if (!e.features?.length) return;
  hoverPopup?.remove();
  hoverPopup = null;
  const props = e.features[0].properties!;
  const coords = (e.features[0].geometry as GeoJSON.Point).coordinates.slice() as [number, number];
  const config = PROJECT_TYPE_CONFIG[props.type as ProjectType];

  activePopup?.remove();
  activePopup = new mapboxgl.Popup({
    closeButton: true,
    maxWidth: '280px',
    className: 'drone-project-popup',
  })
    .setLngLat(coords)
    .setHTML(`
      <div style="font-family:'Inter',system-ui,sans-serif;color:#e8edf2;">
        <div style="font-size:11px;color:${config.color};font-weight:600;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:4px;">
          ${config.icon} ${config.label}
        </div>
        <div style="font-size:14px;font-weight:700;margin-bottom:4px;">${props.name}</div>
        <div style="font-size:11px;color:#94a3b8;margin-bottom:6px;">${props.org}</div>
        <div style="font-size:12px;line-height:1.4;color:#cbd5e1;margin-bottom:8px;">${props.description}</div>
        <a href="${props.url}" target="_blank" rel="noopener"
           style="font-size:11px;color:${config.color};text-decoration:none;">
          Learn more →
        </a>
      </div>
    `)
    .addTo(e.target);
}

// ── Legend ──────────────────────────────────────────────────────────────────

function showLegend() {
  if (legendEl) { legendEl.style.display = ''; return; }

  legendEl = document.createElement('div');
  legendEl.id = 'drone-projects-legend';
  legendEl.className = 'drone-projects-legend';

  const items = Object.entries(PROJECT_TYPE_CONFIG).map(([_, c]) => `
    <div class="dfg-legend-item">
      <span class="dfg-legend-dot" style="background:${c.color}"></span>
      <span>${c.icon} ${c.label}</span>
    </div>
  `).join('');

  legendEl.innerHTML = `
    <div class="dfg-legend-title">Drones For Good</div>
    <div class="dfg-legend-subtitle">Humanitarian & conservation drone projects worldwide</div>
    <div class="dfg-legend-items">${items}</div>
    <div class="dfg-legend-footer">
      Data sourced from HOTOSM, UNICEF, UNHCR, WFP, Zipline, WWF, GCTDF, and others.
      <br>Know a project we're missing? <a href="https://africandrone.org/contact-us" target="_blank" rel="noopener">Let us know.</a>
    </div>
  `;

  document.getElementById('map')!.appendChild(legendEl);
}

function hideLegend() {
  if (legendEl) legendEl.style.display = 'none';
}

// ── Map layer ─────────────────────────────────���────────────────────────────

export async function addDroneProjectsLayer(map: mapboxgl.Map): Promise<void> {
  const data = await loadProjects();

  if (!map.getSource(SOURCE_ID)) {
    map.addSource(SOURCE_ID, { type: 'geojson', data });
  }

  // Build color match expression
  const colorExpr: any[] = ['match', ['get', 'type']];
  for (const [type, config] of Object.entries(PROJECT_TYPE_CONFIG)) {
    colorExpr.push(type, config.color);
  }
  colorExpr.push('#94a3b8'); // fallback

  if (!map.getLayer(LAYER_PULSE)) {
    // Outer pulse ring — subtle glow
    map.addLayer({
      id: LAYER_PULSE,
      type: 'circle',
      source: SOURCE_ID,
      paint: {
        'circle-color': colorExpr as mapboxgl.Expression,
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 1.5, 6, 5, 10, 10, 14],
        'circle-opacity': 0.15,
        'circle-stroke-width': 0,
      },
    });
  }

  if (!map.getLayer(LAYER_ID)) {
    map.addLayer({
      id: LAYER_ID,
      type: 'circle',
      source: SOURCE_ID,
      paint: {
        'circle-color': colorExpr as mapboxgl.Expression,
        'circle-radius': ['interpolate', ['linear'], ['zoom'], 1.5, 3, 5, 5, 10, 8],
        'circle-opacity': 0.9,
        'circle-stroke-width': 1,
        'circle-stroke-color': 'rgba(255,255,255,0.4)',
      },
    });

    // Interactions
    map.on('click', LAYER_ID, onProjectClick);

    // Hover tooltip
    map.on('mouseenter', LAYER_ID, (e: mapboxgl.MapLayerMouseEvent) => {
      map.getCanvas().style.cursor = 'pointer';
      if (!e.features?.length) return;
      const props = e.features[0].properties!;
      const coords = (e.features[0].geometry as GeoJSON.Point).coordinates.slice() as [number, number];
      const config = PROJECT_TYPE_CONFIG[props.type as ProjectType];

      hoverPopup?.remove();
      hoverPopup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        maxWidth: '240px',
        className: 'drone-project-hover',
        offset: 12,
      })
        .setLngLat(coords)
        .setHTML(`
          <div style="font-family:'Inter',system-ui,sans-serif;color:#e8edf2;">
            <div style="font-size:10px;color:${config.color};font-weight:600;text-transform:uppercase;letter-spacing:0.4px;">${config.icon} ${config.label}</div>
            <div style="font-size:13px;font-weight:700;margin-top:2px;">${props.name}</div>
            <div style="font-size:10px;color:#94a3b8;margin-top:1px;">${props.org}</div>
          </div>
        `)
        .addTo(map);
    });

    map.on('mouseleave', LAYER_ID, () => {
      map.getCanvas().style.cursor = '';
      hoverPopup?.remove();
      hoverPopup = null;
    });
  }

  showLegend();
}

export function removeDroneProjectsLayer(map: mapboxgl.Map): void {
  activePopup?.remove();
  activePopup = null;
  hoverPopup?.remove();
  hoverPopup = null;

  for (const layerId of [LAYER_ID, LAYER_PULSE]) {
    if (map.getLayer(layerId)) {
      map.off('click', layerId, onProjectClick);
      map.removeLayer(layerId);
    }
  }
  if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);

  hideLegend();
}
